import React from 'react';
import { Alert, Button, Descriptions, Space, Table, Tag, Typography } from 'antd';
import { CheckCircleOutlined, CloudUploadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import type {
  ActivationDecision,
  ModelQualificationRun,
  ModelPublishedQualification,
  ModelValidationEvidenceResponse,
  QualificationStage,
  QualificationStageName,
} from '../types';
import { CompactStatusGlyph, resolveCompactStatusTone } from './CompactStatus';

const { Text, Title } = Typography;

const STAGE_ORDER: QualificationStageName[] = [
  'uploaded',
  'package_validated',
  'live_validated',
  'cross_engine_passed',
  'platform_qualified',
  'clinical_validated',
];

const STAGE_LABELS: Record<QualificationStageName, string> = {
  uploaded: '模型包上传',
  package_validated: '包结构与哈希',
  live_validated: '代码加载与场景运行',
  cross_engine_passed: '双引擎逐轨迹一致性',
  platform_qualified: '平台技术资格',
  clinical_validated: '临床回顾性验证',
};

const STAGE_OBJECTIVES: Record<QualificationStageName, string> = {
  uploaded: '冻结模型包身份与内容哈希',
  package_validated: '校验 manifest、产物路径、文件哈希与来源自检',
  live_validated: '加载 rxode2/posologyr、编译 mrgsolve，并执行全部测试场景',
  cross_engine_passed: '比较相同给药与协变量下的 AUC、Cmax 和完整浓度轨迹',
  platform_qualified: '汇总技术门槛；不包含临床有效性结论',
  clinical_validated: '使用平台临床数据集评价预测偏差与覆盖率',
};

const STATUS_LABELS: Record<string, string> = {
  not_started: '尚未运行',
  uploaded: '已上传',
  qualification_running: '资格验证运行中',
  package_invalid: '模型包无效',
  script_failed: '技术验证未通过',
  platform_qualified: '平台技术资格通过',
  clinical_validated: '临床验证通过',
  legacy_accepted: '历史模型兼容',
  override_active: '审核覆盖生效',
};

const R_STAGE_LABELS: Record<string, string> = {
  manifest: 'Manifest 解析',
  artifacts: '候选产物定位',
  debug_harness: '双引擎调试脚本契约',
  residual_contract: '命名残差参数契约',
  reference_vectors: '参考值声明与包内自洽',
  posologyr_live: 'R / rxode2 模型加载',
  scenario_coverage: '标准场景覆盖声明',
  map_smoke: 'MAP 管线执行烟雾测试',
  method_smoke: '支持方法逐项执行',
  mrgsolve_live: 'C++ / mrgsolve 编译',
  nca_summary: '标准场景 PK 汇总',
  independent_reference: '平台重算参考证据',
  cross_engine: '双引擎数值一致性',
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const asRecord = (value: unknown): Record<string, unknown> => (
  isRecord(value) ? value : {}
);

const asNumber = (value: unknown): number | undefined => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const asRecordArray = (value: unknown): Record<string, unknown>[] => (
  Array.isArray(value) ? value.filter(isRecord) : []
);

const statusColor = (status: string) => {
  if (['passed', 'platform_qualified', 'clinical_validated'].includes(status)) return 'green';
  if (['warning', 'qualification_running'].includes(status)) return 'orange';
  if (['failed', 'source_rejected', 'package_invalid', 'script_failed'].includes(status)) return 'red';
  if (status === 'skipped') return 'default';
  return 'blue';
};

const formatNumber = (value: unknown, digits = 4) => {
  const number = asNumber(value);
  if (number === undefined) return '-';
  if (number !== 0 && Math.abs(number) < 0.0001) return number.toExponential(2);
  return number.toLocaleString('zh-CN', { maximumFractionDigits: digits });
};

const formatPercent = (value: unknown) => {
  const number = asNumber(value);
  return number === undefined ? '-' : `${formatNumber(number * 100, 4)}%`;
};

const formatRecord = (value: unknown) => {
  const record = asRecord(value);
  const entries = Object.entries(record);
  if (!entries.length) return '-';
  return entries.map(([key, item]) => {
    const rendered = isRecord(item) || Array.isArray(item) ? JSON.stringify(item) : String(item);
    return `${key}: ${rendered}`;
  }).join('; ');
};

const stageMessages = (stage: QualificationStage, key: 'errors' | 'warnings') => (
  Array.isArray(stage[key]) ? stage[key] : []
);

interface StageRow {
  key: string;
  name: string;
  objective: string;
  stage: QualificationStage;
  evidence: string;
}

interface ExecutionRow {
  key: string;
  name: string;
  status: string;
  evidence: string;
  errors: string[];
  warnings: string[];
}

interface ScenarioRow {
  key: string;
  label: string;
  patientData: Record<string, unknown>;
  dosingEvents: Record<string, unknown>[];
  endTimeH?: number;
  delta?: number;
  coverageTags: string[];
  rxode2: Record<string, unknown>;
  mrgsolve: Record<string, unknown>;
  comparison: Record<string, unknown>;
  tolerance?: number;
}

const executionEvidence = (name: string, checks: Record<string, unknown>) => {
  if (name === 'reference_vectors') {
    const basis = asRecord(checks.reference_basis);
    return `参考项 ${formatNumber(checks.total, 0)}；静态自洽 ${formatNumber(checks.static_self_consistency_count, 0)}；`
      + `可由平台重算 ${formatNumber(checks.platform_evaluable_count, 0)}；来源 ${String(basis.type || 'unspecified')}`;
  }
  if (name === 'posologyr_live') {
    const theta = Array.isArray(checks.theta) ? checks.theta.length : 0;
    const outputs = Array.isArray(checks.outputs) ? checks.outputs.length : 0;
    return `theta ${theta} 项；输出/状态 ${outputs} 项`;
  }
  if (name === 'map_smoke') {
    const ids = Array.isArray(checks.scenario_ids) ? checks.scenario_ids.join(', ') : '-';
    const source = String(checks.source || 'unknown');
    return `场景 ${formatNumber(checks.scenario_count, 0)} 个：${ids}；`
      + `内部观测 ${formatNumber(checks.observation_count, 0)} 个；个体参数 ${formatNumber(checks.individual_parameter_count, 0)} 项；来源 ${source}（仅执行证明）`;
  }
  if (name === 'scenario_coverage') {
    const required = Array.isArray(checks.required_tags) ? checks.required_tags.join(', ') : '-';
    const observed = Array.isArray(checks.observed_tags) ? checks.observed_tags.join(', ') : '-';
    return `场景 ${formatNumber(checks.scenario_count, 0)} 个；要求 ${required}；已声明 ${observed}；覆盖标签本身不是临床验证`;
  }
  if (name === 'method_smoke') {
    const declared = Array.isArray(checks.declared_methods) ? checks.declared_methods.join(', ') : '-';
    const exercised = Array.isArray(checks.exercised_methods) ? checks.exercised_methods.join(', ') : '-';
    return `声明方法 ${declared}；实际执行 ${exercised}`;
  }
  if (name === 'independent_reference') {
    return `平台重算 ${formatNumber(checks.evaluated, 0)} 项；证据级别 ${String(checks.evidence_level || 'not_machine_evaluable')}`;
  }
  if (name === 'mrgsolve_live') {
    const parameters = Array.isArray(checks.parameters) ? checks.parameters.length : 0;
    const compartments = Array.isArray(checks.compartments) ? checks.compartments.length : 0;
    return `参数 ${parameters} 项；房室/状态 ${compartments} 项`;
  }
  if (name === 'nca_summary') {
    return `完成 ${formatNumber(checks.scenario_count, 0)} 个场景的 AUC、Cmax 与轨迹汇总`;
  }
  return formatRecord(checks);
};

const scenarioRows = (
  liveChecks: Record<string, unknown>,
  crossChecks: Record<string, unknown>,
): ScenarioRow[] => {
  const engineMetrics = asRecord(liveChecks.engine_metrics);
  const nca = asRecord(engineMetrics.nca);
  const ncaScenarios = asRecord(nca.scenarios);
  const crossScenarios = asRecord(crossChecks.scenarios);
  const ids = Array.from(new Set([
    ...Object.keys(ncaScenarios),
    ...Object.keys(crossScenarios),
  ]));
  const tolerance = asNumber(crossChecks.tolerance);

  if (!ids.length && (isRecord(nca.rxode2) || isRecord(nca.mrgsolve))) {
    ids.push('default');
    ncaScenarios.default = {
      label: '默认场景',
      rxode2: asRecord(nca.rxode2),
      mrgsolve: asRecord(nca.mrgsolve),
    };
    crossScenarios.default = crossChecks;
  }

  return ids.map((id) => {
    const ncaScenario = asRecord(ncaScenarios[id]);
    return {
      key: id,
      label: String(ncaScenario.label || id),
      patientData: asRecord(ncaScenario.patient_data),
      dosingEvents: asRecordArray(ncaScenario.dosing_events),
      endTimeH: asNumber(ncaScenario.end_time_h),
      delta: asNumber(ncaScenario.delta),
      coverageTags: Array.isArray(ncaScenario.coverage_tags)
        ? ncaScenario.coverage_tags.map(String)
        : [],
      rxode2: asRecord(ncaScenario.rxode2),
      mrgsolve: asRecord(ncaScenario.mrgsolve),
      comparison: asRecord(crossScenarios[id]),
      tolerance,
    };
  });
};

const doseSummary = (row: ScenarioRow) => {
  if (!row.dosingEvents.length) {
    return `模拟 ${formatNumber(row.endTimeH, 2)} h；步长 ${formatNumber(row.delta, 3)} h`;
  }
  const first = row.dosingEvents[0];
  const infusion = asNumber(first.infusion_h) || 0;
  return `${row.dosingEvents.length} 次；首剂 ${formatNumber(first.dose_mg, 2)} mg`
    + `${infusion > 0 ? ` / ${formatNumber(infusion, 2)} h` : ''}`
    + `；模拟 ${formatNumber(row.endTimeH, 2)} h`;
};

const scenarioPassed = (row: ScenarioRow) => {
  if (row.tolerance === undefined) return undefined;
  const metrics = [
    row.comparison.auc_relative_difference,
    row.comparison.cmax_relative_difference,
    row.comparison.normalized_rmse,
    row.comparison.max_scaled_difference,
  ].map(asNumber).filter((value): value is number => value !== undefined);
  return metrics.length > 0 ? metrics.every((value) => value <= row.tolerance!) : undefined;
};

export interface ModelQualificationStatusProps {
  qualificationStatus: string;
  reviewStatus: string;
  latestRun?: ModelQualificationRun;
  clinicalEvidence?: ModelValidationEvidenceResponse;
  activationDecision?: ActivationDecision;
  activationRunId?: string | null;
  publishedQualification?: ModelPublishedQualification | null;
  onPublish?: (runId?: string) => void;
  onActivate?: (runId?: string) => void;
  onRequestOverride?: () => void;
  activating?: boolean;
  publishing?: boolean;
}

const ModelQualificationStatus: React.FC<ModelQualificationStatusProps> = ({
  qualificationStatus,
  reviewStatus,
  latestRun,
  clinicalEvidence,
  activationDecision,
  activationRunId,
  publishedQualification,
  onPublish,
  onActivate,
  onRequestOverride,
  activating = false,
  publishing = false,
}) => {
  const stageResults = latestRun?.stage_results || {};
  const packageChecks = asRecord(stageResults.package_validated?.checks);
  const offlineValidation = asRecord(packageChecks.offline_validation);
  const offlineStages = asRecord(offlineValidation.stages);
  const liveChecks = asRecord(stageResults.live_validated?.checks);
  const rStages = asRecord(liveChecks.r_stages);
  const crossChecks = asRecord(stageResults.cross_engine_passed?.checks);
  const platformChecks = asRecord(stageResults.platform_qualified?.checks);
  const scenarios = scenarioRows(liveChecks, crossChecks);
  const clinicalSummary = clinicalEvidence?.summaries[0];
  const failureCategory = String(liveChecks.failure_category || '');
  const infrastructureFailure = failureCategory === 'infrastructure';
  const durationMs = asNumber(liveChecks.duration_ms);

  const knownRows = latestRun
    ? STAGE_ORDER.map((name): StageRow => {
      const stage = stageResults[name] || {
        status: name === 'clinical_validated' && qualificationStatus === 'clinical_validated'
          ? 'passed'
          : 'not_started',
        errors: [],
        warnings: [],
      };
      let evidence = '-';
      if (name === 'uploaded') evidence = `包哈希 ${latestRun.package_sha256}`;
      if (name === 'package_validated') evidence = 'manifest、产物路径、内容哈希与来源自检均已记录';
      if (name === 'live_validated') {
        evidence = `${formatNumber(scenarios.length, 0)} 个场景；耗时 ${formatNumber(durationMs, 0)} ms`;
      }
      if (name === 'cross_engine_passed') {
        evidence = `阈值 ${formatPercent(crossChecks.tolerance)}；最坏 NRMSE ${formatPercent(crossChecks.normalized_rmse)}`
          + `；最大轨迹差 ${formatPercent(crossChecks.max_scaled_difference)}`;
      }
      if (name === 'platform_qualified') {
        evidence = `范围 ${String(platformChecks.qualification_scope || 'technical')}；场景 ${formatNumber(platformChecks.scenario_count, 0)} 个；不包含临床验证`;
      }
      if (name === 'clinical_validated') {
        evidence = clinicalSummary
          ? `受试者 ${formatNumber(clinicalSummary.n_subjects, 0)}；有效预测 ${formatNumber(clinicalSummary.n_predictions, 0)}；RMSE ${formatNumber(clinicalSummary.rmse)}`
          : '尚无平台临床数据集结果';
      }
      return {
        key: name,
        name: STAGE_LABELS[name],
        objective: STAGE_OBJECTIVES[name],
        stage,
        evidence,
      };
    })
    : [];
  const extraRows = Object.entries(stageResults)
    .filter(([name]) => !STAGE_ORDER.includes(name as QualificationStageName))
    .map(([name, stage]): StageRow => ({
      key: name,
      name,
      objective: '扩展验证阶段',
      stage,
      evidence: formatRecord(stage.checks),
    }));
  const stageRows = [...knownRows, ...extraRows];
  const offlineContractRows = ['debug_harness', 'residual_contract']
    .filter((name) => isRecord(offlineStages[name]))
    .map((name): ExecutionRow => {
      const stage = asRecord(offlineStages[name]);
      const errors = Array.isArray(stage.errors) ? stage.errors.map(String) : [];
      const warnings = Array.isArray(stage.warnings) ? stage.warnings.map(String) : [];
      return {
        key: `offline:${name}`,
        name: R_STAGE_LABELS[name] || name,
        status: String(stage.status || 'unknown'),
        evidence: executionEvidence(name, asRecord(stage.checks)),
        errors,
        warnings,
      };
    });
  const liveExecutionRows = Object.entries(rStages).map(([name, value]): ExecutionRow => {
    const stage = asRecord(value);
    const errors = Array.isArray(stage.errors) ? stage.errors.map(String) : [];
    const warnings = Array.isArray(stage.warnings) ? stage.warnings.map(String) : [];
    return {
      key: name,
      name: R_STAGE_LABELS[name] || name,
      status: String(stage.status || 'unknown'),
      evidence: executionEvidence(name, asRecord(stage.checks)),
      errors,
      warnings,
    };
  });
  const executionRows = [...offlineContractRows, ...liveExecutionRows];
  const publishedRunId = publishedQualification?.qualification_run_id;
  const currentPackagePublished = Boolean(
    latestRun
    && publishedQualification?.package_id === latestRun.package_id
    && publishedQualification?.package_sha256 === latestRun.package_sha256,
  );
  const publishAllowed = Boolean(
    onPublish
    && ['platform_qualified', 'clinical_validated'].includes(qualificationStatus)
    && latestRun?.status === 'platform_qualified'
    && reviewStatus === 'approved'
    && !currentPackagePublished,
  );
  const activationAllowed = Boolean(
    activationDecision?.allowed
    && onActivate
    && currentPackagePublished,
  );
  const overrideEligible = Boolean(
    onRequestOverride
    && activationDecision
    && !activationDecision.allowed
    && ['qualification_required', 'new_model_qualification_required'].includes(
      activationDecision.reason_code,
    ),
  );

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="资格状态">
          <Tag color={statusColor(qualificationStatus)}>
            {STATUS_LABELS[qualificationStatus] || qualificationStatus}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="审核状态">
          <Tag color={reviewStatus === 'approved' ? 'green' : 'orange'}>{reviewStatus}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="模型库发布状态" span={2}>
          <Space size={8} wrap>
            <Tag color={currentPackagePublished ? 'green' : publishedRunId ? 'orange' : 'default'}>
              {currentPackagePublished ? '当前脚本版本已发布' : publishedRunId ? '当前脚本版本待发布' : '尚未发布'}
            </Tag>
            {publishedQualification?.package_version ? (
              <Text type="secondary">已发布包版本 {publishedQualification.package_version}</Text>
            ) : null}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="最新资格运行">{latestRun?.id || '-'}</Descriptions.Item>
        <Descriptions.Item label="运行耗时">
          {durationMs === undefined ? '-' : `${formatNumber(durationMs, 0)} ms`}
        </Descriptions.Item>
        <Descriptions.Item label="R 运行环境" span={2}>
          {latestRun ? formatRecord(latestRun.r_package_versions) : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="激活资格" span={2}>
          <Space size={8} wrap>
            <Tag color={activationDecision?.allowed ? 'green' : 'red'}>
              {activationDecision?.allowed ? '可激活' : '不可激活'}
            </Tag>
            <Text type={activationDecision?.allowed ? 'success' : 'secondary'}>
              {activationDecision?.message || '正在加载激活预检结果'}
            </Text>
          </Space>
        </Descriptions.Item>
      </Descriptions>

      {stageRows.length ? (
        <>
          <Title level={5} style={{ margin: 0 }}>资格判定链</Title>
          <Table<StageRow>
            rowKey="key"
            size="small"
            pagination={false}
            scroll={{ x: 960 }}
            dataSource={stageRows}
            columns={[
              { title: '阶段', dataIndex: 'name', width: 180 },
              { title: '验证目标', dataIndex: 'objective', width: 280 },
              {
                title: '状态',
                width: 72,
                align: 'center' as const,
                render: (_, row) => (
                  <CompactStatusGlyph label="资格阶段" value={row.stage.status} tone={resolveCompactStatusTone(row.stage.status)} />
                ),
              },
              { title: '量化证据', dataIndex: 'evidence', width: 360 },
              {
                title: '异常',
                width: 320,
                render: (_, row) => {
                  const errors = stageMessages(row.stage, 'errors');
                  const warnings = stageMessages(row.stage, 'warnings');
                  if (!errors.length && !warnings.length) return '-';
                  return (
                    <Space direction="vertical" size={2}>
                      {errors.map((item) => <Text type="danger" key={`e-${item}`}>{item}</Text>)}
                      {warnings.map((item) => <Text type="warning" key={`w-${item}`}>{item}</Text>)}
                    </Space>
                  );
                },
              },
            ]}
          />
        </>
      ) : (
        <Alert type="info" showIcon message="该模型包尚无资格运行记录" />
      )}

      {executionRows.length ? (
        <>
          <Title level={5} style={{ margin: 0 }}>代码与运行证据</Title>
          <Table<ExecutionRow>
            rowKey="key"
            size="small"
            pagination={false}
            dataSource={executionRows}
            columns={[
              { title: '检查项', dataIndex: 'name', width: 220 },
              {
                title: '状态',
                dataIndex: 'status',
                width: 72,
                align: 'center' as const,
                render: (status: string) => <CompactStatusGlyph label="运行检查" value={status} tone={resolveCompactStatusTone(status)} />,
              },
              { title: '实际证据', dataIndex: 'evidence' },
              {
                title: '异常',
                width: 320,
                render: (_, row) => row.errors.length || row.warnings.length ? (
                  <Space direction="vertical" size={2}>
                    {row.errors.map((item) => <Text type="danger" key={`e-${item}`}>{item}</Text>)}
                    {row.warnings.map((item) => <Text type="warning" key={`w-${item}`}>{item}</Text>)}
                  </Space>
                ) : '-',
              },
            ]}
          />
        </>
      ) : null}

      {scenarios.length ? (
        <>
          <Title level={5} style={{ margin: 0 }}>标准场景双引擎一致性</Title>
          <Alert
            type="info"
            showIcon
            message="这些指标评价两套脚本是否实现一致"
            description="AUC差、Cmax差、轨迹 NRMSE 和最大轨迹差用于发现 R 与 C++ 实现偏差；数值接近不能单独证明模型公式、参数来源或临床预测正确。"
          />
          <Table<ScenarioRow>
            rowKey="key"
            size="small"
            pagination={false}
            scroll={{ x: 1500 }}
            dataSource={scenarios}
            columns={[
              {
                title: '场景',
                fixed: 'left',
                width: 150,
                render: (_, row) => <Space direction="vertical" size={0}><Text strong>{row.label}</Text><Text type="secondary">{row.key}</Text></Space>,
              },
              {
                title: '覆盖标签',
                width: 180,
                render: (_, row) => row.coverageTags.length
                  ? <Space size={[4, 4]} wrap>{row.coverageTags.map((tag) => <Tag key={tag}>{tag}</Tag>)}</Space>
                  : '-',
              },
              { title: '协变量', width: 230, render: (_, row) => formatRecord(row.patientData) },
              { title: '给药 / 模拟', width: 230, render: (_, row) => doseSummary(row) },
              { title: 'R AUC', width: 110, render: (_, row) => formatNumber(row.rxode2.auc) },
              { title: 'CPP AUC', width: 110, render: (_, row) => formatNumber(row.mrgsolve.auc) },
              { title: 'AUC差', width: 110, render: (_, row) => formatPercent(row.comparison.auc_relative_difference) },
              { title: 'R Cmax', width: 110, render: (_, row) => formatNumber(row.rxode2.cmax) },
              { title: 'CPP Cmax', width: 110, render: (_, row) => formatNumber(row.mrgsolve.cmax) },
              { title: 'Cmax差', width: 110, render: (_, row) => formatPercent(row.comparison.cmax_relative_difference) },
              { title: '轨迹 NRMSE', width: 130, render: (_, row) => formatPercent(row.comparison.normalized_rmse) },
              { title: '最大轨迹差', width: 130, render: (_, row) => formatPercent(row.comparison.max_scaled_difference) },
              { title: '阈值', width: 100, render: (_, row) => formatPercent(row.tolerance) },
              {
                title: '判定',
                fixed: 'right',
                width: 90,
                render: (_, row) => {
                  const passed = scenarioPassed(row);
                  return passed === undefined ? '-' : <Tag color={passed ? 'green' : 'red'}>{passed ? '通过' : '失败'}</Tag>;
                },
              },
            ]}
          />
        </>
      ) : null}

      <Title level={5} style={{ margin: 0 }}>临床回顾性验证</Title>
      {clinicalSummary ? (
        <Descriptions bordered size="small" column={3}>
          <Descriptions.Item label="状态">{String(clinicalSummary.overall_status || clinicalEvidence?.validation_status || '-')}</Descriptions.Item>
          <Descriptions.Item label="受试者">{formatNumber(clinicalSummary.n_subjects, 0)}</Descriptions.Item>
          <Descriptions.Item label="有效预测">{formatNumber(clinicalSummary.n_predictions, 0)}</Descriptions.Item>
          <Descriptions.Item label="MPE%">{formatNumber(clinicalSummary.mpe_percent)}</Descriptions.Item>
          <Descriptions.Item label="MAPE%">{formatNumber(clinicalSummary.mape)}</Descriptions.Item>
          <Descriptions.Item label="RMSE">{formatNumber(clinicalSummary.rmse)}</Descriptions.Item>
          <Descriptions.Item label="F20%">{formatNumber(clinicalSummary.f20)}</Descriptions.Item>
          <Descriptions.Item label="F30%">{formatNumber(clinicalSummary.f30)}</Descriptions.Item>
          <Descriptions.Item label="MAP成功率%">{formatNumber(clinicalSummary.map_success_rate)}</Descriptions.Item>
          <Descriptions.Item label="结论" span={3}>{String(clinicalSummary.conclusion || '-')}</Descriptions.Item>
        </Descriptions>
      ) : (
        <Alert
          type="warning"
          showIcon
          message="尚无平台临床数据集验证结果"
          description="当前结果只能形成平台技术资格证据，不能形成外部临床验证结论。"
        />
      )}

      {qualificationStatus === 'script_failed' ? (
        infrastructureFailure ? (
          <Alert
            type="warning"
            showIcon
            message="验证执行环境失败，可重试"
            description={latestRun?.errors.join('; ') || '资格验证 worker 未在超时时间内返回结果'}
          />
        ) : (
          <Alert type="error" showIcon message="代码加载、编译、运行或数值一致性验证未通过，当前模型不可激活" />
        )
      ) : null}
      <Space wrap>
        {publishAllowed ? (
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            aria-label="Publish model"
            loading={publishing}
            onClick={() => onPublish?.(latestRun?.id)}
          >
            发布到模型库
          </Button>
        ) : null}
        {activationAllowed ? (
          <Button
            icon={<CheckCircleOutlined />}
            aria-label="Activate model"
            loading={activating}
            onClick={() => onActivate?.(activationRunId || undefined)}
          >
            批准全局测试
          </Button>
        ) : null}
        {overrideEligible ? (
          <Button
            icon={<SafetyCertificateOutlined />}
            aria-label="Request activation override"
            onClick={onRequestOverride}
          >
            审核覆盖激活
          </Button>
        ) : null}
      </Space>
    </Space>
  );
};

export default ModelQualificationStatus;
